import React, { ReactNode, MouseEvent, ButtonHTMLAttributes } from 'react';
import styled, { css } from 'styled-components';
import noop from 'utils/noop';
import { pxToRem } from 'utils/convert';
import { colors, button, insetShadow, spacing, easing, duration } from 'ui/theme';
import { GroupButtonSizes, GROUP_BUTTON_SIZES } from './constants';

type GroupButtonStates = {
  DISABLED: string;
  SELECTED: string;
};
const GROUP_BUTTON_STATES: GroupButtonStates = {
  DISABLED: 'disabled',
  SELECTED: 'selected',
};

const variantStyles = {
  [GROUP_BUTTON_STATES.DISABLED]: css`
    background: ${colors.grey1};
    color: ${colors.textDisabled};
    cursor: not-allowed;
    pointer-events: none;

    path {
      stroke: ${colors.grey25};
    }

    :hover {
      box-shadow: none;
    }
  `,
  [GROUP_BUTTON_STATES.SELECTED]: css`
    background: ${colors.purple10};
    border: ${spacing.single} solid ${colors.purple20};
    color: ${colors.purple90};

    path {
      stroke: ${colors.purple90};
    }
  `,
};

const sizeStyles = {
  [GROUP_BUTTON_SIZES.SMALL]: css`
    height: ${pxToRem(32)};
    padding-left: ${spacing.threeQuarter};
    padding-right: ${spacing.threeQuarter};
  `,
  [GROUP_BUTTON_SIZES.MEDIUM]: css`
    height: ${pxToRem(36)};
    padding-left: ${spacing.threeQuarter};
    padding-right: ${spacing.threeQuarter};
  `,
  [GROUP_BUTTON_SIZES.LARGE]: css`
    height: ${pxToRem(40)};
    padding-left: ${spacing.base};
    padding-right: ${spacing.base};
  `,
};

type StyledGroupButtonProps = ButtonHTMLAttributes<HTMLButtonElement> & {
  buttonSize: GroupButtonSizes;
  isSelected: boolean;
};
const StyledGroupButton = styled.button<StyledGroupButtonProps>`
  ${button}
  align-items: center;
  background: ${colors.white};
  border: solid 1px ${colors.grey15};
  color: ${colors.textPrimary};
  cursor: pointer;
  display: flex;
  justify-content: center;
  outline: none;
  transition: background ${duration.short} ${easing.entrance};

  & + & {
    border-left-color: transparent;
  }

  :first-of-type {
    border-top-left-radius: ${spacing.quarter};
    border-bottom-left-radius: ${spacing.quarter};
  }

  :last-of-type {
    border-top-right-radius: ${spacing.quarter};
    border-bottom-right-radius: ${spacing.quarter};
  }

  path {
    stroke: ${colors.grey60};
  }

  :hover {
    background: ${colors.purple10};
    border: ${spacing.single} solid ${colors.purple20};
    color: ${colors.purple90};

    path {
      stroke: ${colors.purple90};
    }
  }

  && {
    :focus {
      border-color: ${colors.purple50};
    }
  }

  :active {
    ${insetShadow};
  }

  ${({ buttonSize }) => sizeStyles[buttonSize]};
  ${(props) => props['aria-disabled'] && variantStyles[GROUP_BUTTON_STATES.DISABLED]};
  ${({ isSelected }) => isSelected && variantStyles[GROUP_BUTTON_STATES.SELECTED]};
`;

export type GroupButtonProps = React.ComponentPropsWithoutRef<'button'> & {
  /** INHERITED FROM BUTTON GROUP: the size variant */
  buttonSize?: (typeof GROUP_BUTTON_SIZES)['MEDIUM' | 'LARGE'];
  /** child content for the button */
  children: ReactNode;
  /** INHERITED FROM BUTTON GROUP: state tracking fn */
  handleClick?: (val: string) => void;
  /** a unique identifier value to track selection */
  id?: string;
  /** if the button is disabled */
  isDisabled?: boolean;
  /** INHERITED FROM BUTTON GROUP: if the overall button group is disabled */
  isGroupDisabled?: boolean;
  /** if the button is read only */
  isReadOnly?: boolean;
  /** INHERITED FROM BUTTON GROUP: if the button is current selected */
  isSelected?: boolean;
  /** optional fn to run when deselecting an option */
  onDeselect?: (e: MouseEvent) => void;
  /** fn to run when clicking a button, won't run if deselect fn exist for selected button click */
  onSelect?: (e: MouseEvent) => void;
};

/**
 * @deprecated - Use ButtonGroup from ui/buttonGroupV2.
 */
export const GroupButton = ({
  buttonSize = GROUP_BUTTON_SIZES.MEDIUM,
  children,
  handleClick = noop,
  id = '',
  isDisabled = false,
  isGroupDisabled = false,
  isReadOnly = false,
  isSelected = false,
  onDeselect, // don't default this, its presence enables optional deselect functionality
  onSelect = noop,
  ...props
}: GroupButtonProps) => {
  const disabled = isGroupDisabled || isDisabled;

  return (
    <StyledGroupButton
      aria-pressed={isSelected}
      aria-disabled={disabled}
      buttonSize={buttonSize}
      data-test={`group-button-${id}`}
      disabled={isGroupDisabled}
      id={id}
      isSelected={disabled && !isReadOnly ? false : isSelected}
      onClick={(e) => {
        if (disabled) {
          return; // eject early on clicks for disabled elements
        }

        handleClick(id); // always call the state tracker from the group

        if (isSelected && onDeselect) {
          onDeselect(e); // if a button has a deselect fn, fire that on deselect
        } else {
          onSelect(e);
        }
      }}
      type="button"
      {...props}
    >
      {children}
    </StyledGroupButton>
  );
};

export default GroupButton;
